import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useToast } from "../ToastContext";

export default function QRHealthMonitor({ user }) {
  const { addToast } = useToast();
  const [qrs, setQrs] = useState([]);
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState({});

  useEffect(() => {
    if (!user?.id) return;
    supabase.from("qr_codes")
      .select("id, name, destination_url, qr_mode, total_scans")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => setQrs(data || []));
  }, [user?.id]);

  const checkAll = async () => {
    setChecking(true);
    const newResults = {};
    for (const qr of qrs) {
      if (!qr.destination_url || qr.qr_mode === "vcard" || qr.qr_mode === "wifi" || qr.qr_mode === "whatsapp") {
        newResults[qr.id] = { status: "skip", label: "N/A", healthy: null };
        continue;
      }
      try {
        const res = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(qr.destination_url)}`, {
          signal: AbortSignal.timeout(8000)
        });
        const data = await res.json();
        const statusCode = data.status?.http_code || 200;
        const healthy = statusCode >= 200 && statusCode < 400;
        newResults[qr.id] = { status: statusCode, healthy, label: healthy ? "Healthy" : "Dead Link" };

        // Log to DB
        await supabase.from("qr_health_logs").insert([{
          qr_id: qr.id,
          status_code: statusCode,
          is_healthy: healthy
        }]);
      } catch (err) {
        newResults[qr.id] = { status: "error", healthy: false, label: "Unreachable" };
        await supabase.from("qr_health_logs").insert([{
          qr_id: qr.id,
          is_healthy: false,
          error: err.message
        }]);
      }
    }
    setResults(newResults);
    setChecking(false);
    const deadCount = Object.values(newResults).filter(r => r.healthy === false).length;
    if (deadCount > 0) addToast(`⚠️ ${deadCount} QR codes have dead links!`, "error");
    else addToast("All QR codes are healthy! ✅", "success");
  };

  const healthyCount = Object.values(results).filter(r => r.healthy === true).length;
  const deadCount = Object.values(results).filter(r => r.healthy === false).length;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-black text-white uppercase italic tracking-tighter">QR Health Monitor</h1>
          <p className="text-gray-500 text-[10px] uppercase tracking-widest mt-1">Check if your destination URLs are working</p>
        </div>
        <button onClick={checkAll} disabled={checking || !qrs.length}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-xl text-[10px] font-black uppercase text-white transition-all flex items-center gap-2">
          {checking ? (
            <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Checking...</>
          ) : "🔍 Check All Now"}
        </button>
      </div>

      {Object.keys(results).length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
            <p className="text-[9px] font-black uppercase text-gray-500">Checked</p>
            <p className="text-3xl font-black text-white">{Object.keys(results).length}</p>
          </div>
          <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 text-center">
            <p className="text-[9px] font-black uppercase text-gray-500">Healthy</p>
            <p className="text-3xl font-black text-green-400">{healthyCount}</p>
          </div>
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 text-center">
            <p className="text-[9px] font-black uppercase text-gray-500">Dead Links</p>
            <p className="text-3xl font-black text-red-400">{deadCount}</p>
          </div>
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-[32px] overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-sm font-black uppercase tracking-widest">All QR Codes ({qrs.length})</h3>
        </div>
        <div className="divide-y divide-white/5">
          {qrs.length === 0 ? (
            <p className="p-6 text-gray-500 text-sm">No QR codes found.</p>
          ) : qrs.map(qr => {
            const result = results[qr.id];
            return (
              <div key={qr.id} className="flex items-center justify-between px-6 py-4">
                <div className="flex-1 min-w-0 mr-4">
                  <p className="text-white text-sm font-black truncate">{qr.name || "Untitled"}</p>
                  <p className="text-gray-500 text-[10px] truncate">{qr.destination_url || "No URL"}</p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-[10px] text-gray-500 uppercase font-black">{qr.total_scans || 0} scans</span>
                  {result ? (
                    <span className={`text-[9px] font-black uppercase px-3 py-1.5 rounded-full ${result.healthy === true ? "bg-green-500/20 text-green-400" : result.healthy === false ? "bg-red-500/20 text-red-400" : "bg-white/10 text-gray-500"}`}>
                      {result.label}
                    </span>
                  ) : (
                    <span className="text-[9px] font-black uppercase px-3 py-1.5 rounded-full bg-white/5 text-gray-600">
                      Not checked
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}